<template>
  <div class="qz-matching" :class="{ 'qz-answered': isAnswered }">
    <!-- eslint-disable vue/no-v-html -->
    <div class="qz-stem" v-html="view.stem"></div>
    <!-- eslint-enable vue/no-v-html -->
    <!-- ref=container 必须锚在 board 上（评审 F1）：SVG 是 absolute，其 CSS 包含块=最近
         positioned 祖先即 board（relative），线坐标系必须与 SVG 原点同源；锚更外层
         .qz-matching 会让所有连线整体偏移题干高度（详见 computeLine 注释） -->
    <div ref="container" class="qz-matching-board">
      <svg class="qz-matching-svg">
        <line
          v-for="(line, i) in lines"
          :key="i"
          :x1="line.x1"
          :y1="line.y1"
          :x2="line.x2"
          :y2="line.y2"
          :class="lineClass(line.state)"
        />
      </svg>
      <ul class="qz-matching-left">
        <li
          v-for="item in view.left"
          :key="item.id"
          ref="leftItems"
          :data-id="item.id"
          class="qz-matching-item"
          :class="itemClass('left', item.id)"
          role="button"
          :tabindex="isAnswered ? -1 : 0"
          :aria-pressed="picked === item.id || pairs[item.id] ? 'true' : 'false'"
          :aria-label="leftAriaLabel(item)"
          @click="handleLeftClick(item.id)"
          @keydown.enter.prevent="handleLeftClick(item.id)"
          @keydown.space.prevent="handleLeftClick(item.id)"
        >
          {{ item.text }}
        </li>
      </ul>
      <ul class="qz-matching-right">
        <li
          v-for="item in view.right"
          :key="item.id"
          ref="rightItems"
          :data-id="item.id"
          class="qz-matching-item"
          :class="itemClass('right', item.id)"
          role="button"
          :tabindex="isAnswered ? -1 : 0"
          :aria-pressed="isRightPaired(item.id) ? 'true' : 'false'"
          :aria-label="rightAriaLabel(item)"
          @click="handleRightClick(item.id)"
          @keydown.enter.prevent="handleRightClick(item.id)"
          @keydown.space.prevent="handleRightClick(item.id)"
        >
          {{ item.text }}
        </li>
      </ul>
    </div>
    <p v-if="!isAnswered" class="qz-matching-hint" aria-live="polite">
      {{ matchingHint }}
    </p>
    <p class="qz-matching-announcement qz-sr-only" aria-live="polite">
      {{ matchingAnnouncement }}
    </p>
    <div v-if="isAnswered" class="qz-feedback" :class="feedbackStatusClass">
      <div v-if="feedback.right !== 0" class="qz-feedback-result">
        {{ feedback.right === 1 ? '回答正确' : '回答错误' }}
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div v-if="analysisText" class="qz-analysis" v-html="analysisText"></div>
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script>
// v-html 依赖说明：view.stem / feedback.analysis.text 均为服务端 BuildStudentView / 答后反馈
// 接口下发，已经过 bluemonday 消毒（见 saas-api pkg/sanitize），组件内 v-html 渲染是设计内行为。
// left/right 的 item.text 走文本插值（{{ }}），本题型契约未声明选项文本支持富文本，保持纯文本更安全。

// computeLine：基于 .qz-matching-board 内部布局坐标计算 SVG 端点。
// itemBox 的 left/top/width/height 来自 offset*，与 SVG 共用 transform 之前的布局坐标系；
// 外层滚动、iframe 偏移或 H5 伪横屏 rotate 都不会改变这组值。不能用
// getBoundingClientRect() 相减：rect 是 transform 之后的视口坐标，页面旋转 90° 时其轴向已
// 交换，而 SVG line 属性仍使用 transform 之前的本地坐标，两套坐标混算会让线端点悬空。
// side='left' 时取该项右缘中点（连线从左列格子的右边发出）；side='right' 时取左缘中点（连线
// 从右列格子的左边接入）。
export function computeLine (itemBox, side) {
  const y = itemBox.top + itemBox.height / 2
  const x = side === 'left' ? itemBox.left + itemBox.width : itemBox.left
  return { x, y }
}

export default {
  name: 'MatchingRenderer',
  props: {
    view: {
      type: Object,
      required: true
    },
    session: {
      type: Object,
      default: null
    },
    env: {
      type: Object,
      required: true
    },
    feedback: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      // 交互状态机（裁定 1）：
      //   picked = null → tap 左项：picked = 该 leftId（高亮 .qz-picked）；再 tap 同一左项 → 取消 picked
      //   tap 右项（picked 非空）：pairs[picked] = rightId，picked 清空
      //     - 若该右项已被占用：旧对解除，新对建立（一对一约束）
      //   tap 已配对的左项：解除该对，并把 picked 设为该 leftId（方便立即重连）
      //   无 picked 时 tap 右项：无操作
      picked: null,
      pairs: this.restorePairs(),
      lines: [],
      matchingAnnouncement: ''
    }
  },
  computed: {
    isAnswered () {
      return !!this.feedback
    },
    analysisText () {
      return this.feedback && this.feedback.analysis && this.feedback.analysis.text
    },
    feedbackStatusClass () {
      if (!this.isAnswered || this.feedback.right === 0) return ''
      return this.feedback.right === 1 ? 'qz-feedback-right' : 'qz-feedback-wrong'
    },
    // feedback 态下逐对比对用的正解 map（防御非对象形状）
    correctPairs () {
      if (!this.isAnswered || this.feedback.right === 0) return null
      const p = this.feedback.correct_view && this.feedback.correct_view.pairs
      return p && typeof p === 'object' ? p : {}
    },
    matchingHint () {
      if (!this.picked) return '先选择左侧内容，再选择右侧匹配项'
      const item = (this.view.left || []).find(left => left.id === this.picked)
      return `已选「${item ? item.text : '左侧内容'}」，请选择匹配项`
    }
  },
  mounted () {
    this.$nextTick(this.updateLines)
    window.addEventListener('resize', this.updateLines)
  },
  updated () {
    // pairs/picked/feedback 变化、以及 lines 自身变化都会触发这个钩子；updateLines 内部对
    // this.lines 的写入做了内容去重（见 linesEqual 注释），几何不变时不再赋值，因此这里递归
    // 调度 updateLines 不会构成死循环——geometry 稳定后这条链自然收敛
    this.$nextTick(this.updateLines)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateLines)
  },
  methods: {
    restorePairs () {
      const p = this.session && this.session.answer && this.session.answer.pairs
      return p && typeof p === 'object' ? { ...p } : {}
    },
    emitChange () {
      this.$emit('session-changed', {
        // 不可变更新：pairs 每次变化都新建对象，防止宿主持有的旧引用被静默篡改（裁定 2）
        answer: { pairs: { ...this.pairs } },
        complete: Object.keys(this.pairs).length === this.view.left.length
      })
    },
    handleLeftClick (id) {
      if (this.isAnswered) return
      if (Object.prototype.hasOwnProperty.call(this.pairs, id)) {
        // 已配对的左项：解除该对，进入 picked 态方便重连
        const rightId = this.pairs[id]
        const next = { ...this.pairs }
        delete next[id]
        this.pairs = next
        this.picked = id
        this.matchingAnnouncement = `已取消${this.leftText(id)}与 ${this.rightText(rightId)} 的匹配`
        this.emitChange()
        return
      }
      // 未配对：picked 状态切换（再 tap 同项取消）
      this.picked = this.picked === id ? null : id
    },
    handleRightClick (id) {
      if (this.isAnswered) return
      if (!this.picked) return // 无 picked 时 tap 右项无操作
      const leftId = this.picked
      const next = {}
      Object.keys(this.pairs).forEach(k => {
        // 一对一约束：抹掉指向同一 rightId 的旧对（被占用右项的旧对解除）
        if (this.pairs[k] !== id) next[k] = this.pairs[k]
      })
      next[leftId] = id
      this.pairs = next
      this.picked = null
      this.matchingAnnouncement = `已将${this.leftText(leftId)}与 ${this.rightText(id)} 匹配`
      this.emitChange()
    },
    leftText (id) {
      const item = (this.view.left || []).find(left => left.id === id)
      return item ? item.text : '左侧内容'
    },
    rightText (id) {
      const item = (this.view.right || []).find(right => right.id === id)
      return item ? item.text : '右侧内容'
    },
    leftAriaLabel (item) {
      const rightId = this.pairs[item.id]
      if (rightId) return `${item.text}，已匹配 ${this.rightText(rightId)}`
      if (this.picked === item.id) return `${item.text}，已选择，请选择右侧匹配项`
      return `${item.text}，未匹配`
    },
    rightAriaLabel (item) {
      const leftId = Object.keys(this.pairs).find(id => this.pairs[id] === item.id)
      if (leftId) return `${item.text}，已与${this.leftText(leftId)}匹配`
      return `${item.text}，未匹配`
    },
    itemClass (side, id) {
      const cls = {}
      if (side === 'left') {
        if (this.picked === id) cls['qz-picked'] = true
      }
      if (this.isAnswered && this.correctPairs) {
        if (side === 'left' && Object.prototype.hasOwnProperty.call(this.pairs, id)) {
          cls[this.correctPairs[id] === this.pairs[id] ? 'qz-correct' : 'qz-wrong'] = true
        }
        if (side === 'right') {
          const leftId = Object.keys(this.pairs).find(k => this.pairs[k] === id)
          if (leftId !== undefined) {
            cls[this.correctPairs[leftId] === id ? 'qz-correct' : 'qz-wrong'] = true
          }
        }
      }
      return cls
    },
    isRightPaired (id) {
      return Object.keys(this.pairs).some(leftId => this.pairs[leftId] === id)
    },
    lineClass (state) {
      return {
        'qz-line-correct': state === 'correct',
        'qz-line-wrong': state === 'wrong',
        'qz-line-hint': state === 'correct-hint',
        'qz-line-default': state === 'default'
      }
    },
    // 内容比对（非引用比对）：updateLines 每次都会 new 一个 result 数组，如果无条件赋给
    // this.lines，即便坐标/state 完全没变也会因为"新引用"触发 Vue 响应式 setter（Vue2 的
    // reactiveSetter 只做 newVal === value 的浅比较，数组永远判定为"变了"——**零对也触发**：
    // 空 pairs 每轮同样 new 一个 []，[] !== []）、进而重新渲染、触发 updated() 钩子、又调度
    // 一次 updateLines——构成同步 microtask 死循环（$nextTick 基于 Promise，循环体不会让出
    // 宏任务队列，会彻底饿死事件循环，连 Jest 的测试超时定时器都排不上号，表现为进程整体挂死
    // 而非普通的测试失败）。这里在赋值前做一次浅内容比较，几何/state 未变时跳过赋值，从根上
    // 掐断这个反馈环；pairs/picked 真变化时仍会重算一轮然后收敛。
    linesEqual (a, b) {
      if (a.length !== b.length) return false
      return a.every((line, i) => {
        const other = b[i]
        return other &&
          line.x1 === other.x1 && line.y1 === other.y1 &&
          line.x2 === other.x2 && line.y2 === other.y2 &&
          line.state === other.state
      })
    },
    // updateLines：从 pairs（+ feedback 态下补画的漏连/错连正解）算出 lines 数组。
    // jsdom 无布局环境下 offset* 恒返回全 0——这里仍然按流程跑一遍（数量/state 类断言仍然
    // 成立）；坐标公式由 computeLine 纯函数覆盖，transform 场景由真浏览器 e2e 覆盖。
    updateLines () {
      // $refs.container = .qz-matching-board；它是列表项最近的 positioned 祖先，也是 SVG
      // 的 CSS 包含块，因此列表项 offset* 与 SVG line 属性天然同源。
      const container = this.$refs.container
      if (!container) {
        if (this.lines.length) this.lines = []
        return
      }
      const leftEls = this.$refs.leftItems || []
      const rightEls = this.$refs.rightItems || []
      const findEl = (els, id) => els.find(el => el.getAttribute('data-id') === id)
      const layoutBox = el => ({
        left: el.offsetLeft,
        top: el.offsetTop,
        width: el.offsetWidth,
        height: el.offsetHeight
      })

      const buildLine = (leftId, rightId, state) => {
        const leftEl = findEl(leftEls, leftId)
        const rightEl = findEl(rightEls, rightId)
        if (!leftEl || !rightEl) return null
        const p1 = computeLine(layoutBox(leftEl), 'left')
        const p2 = computeLine(layoutBox(rightEl), 'right')
        return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, state }
      }

      const result = []

      if (this.isAnswered && this.correctPairs) {
        // feedback 态：我连的每对逐对判色；正解里我没连中的（漏连或连错导致缺席）补画虚线提示
        Object.keys(this.pairs).forEach(leftId => {
          const rightId = this.pairs[leftId]
          const state = this.correctPairs[leftId] === rightId ? 'correct' : 'wrong'
          const line = buildLine(leftId, rightId, state)
          if (line) result.push(line)
        })
        Object.keys(this.correctPairs).forEach(leftId => {
          const rightId = this.correctPairs[leftId]
          if (this.pairs[leftId] === rightId) return // 已经连对，上面已画实线
          const line = buildLine(leftId, rightId, 'correct-hint')
          if (line) result.push(line)
        })
      } else {
        Object.keys(this.pairs).forEach(leftId => {
          const line = buildLine(leftId, this.pairs[leftId], 'default')
          if (line) result.push(line)
        })
      }

      if (!this.linesEqual(this.lines, result)) {
        this.lines = result
      }
    }
  }
}
</script>

<style src="../styles/base.css"></style>

<style scoped>
.qz-matching {
  position: relative;
}

.qz-matching-board {
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 10%;
}

.qz-matching-svg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.qz-matching-svg line {
  stroke-width: 2;
}

.qz-line-default {
  stroke: var(--qz-primary, #409eff);
}

.qz-line-correct {
  stroke: var(--qz-correct, #67c23a);
}

.qz-line-wrong {
  stroke: var(--qz-wrong, #f56c6c);
}

.qz-line-hint {
  stroke: var(--qz-correct, #67c23a);
  stroke-dasharray: 4 4;
  opacity: 0.6;
}

.qz-matching-left,
.qz-matching-right {
  flex: 1 1 0;
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.qz-matching-item {
  box-sizing: border-box;
  min-height: var(--qz-option-min-height, 0);
  padding: var(--qz-option-padding, 10px 12px);
  margin-bottom: 10px;
  border: 1px solid var(--qz-option-border, #dcdfe6);
  border-radius: var(--qz-option-radius, 6px);
  color: var(--qz-text, #303133);
  background: var(--qz-option-background, transparent);
  box-shadow: var(--qz-option-shadow, none);
  word-break: break-word;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.qz-matching:not(.qz-answered) .qz-matching-item {
  cursor: pointer;
}

.qz-matching:not(.qz-answered) .qz-matching-item:hover {
  border-color: var(--qz-primary, #409eff);
  background: var(--qz-primary-hover-bg, rgba(64, 158, 255, 0.05));
  box-shadow: 0 4px 10px var(--qz-primary-shadow, rgba(64, 158, 255, 0.08));
  transform: translateY(-1px);
}

.qz-matching:not(.qz-answered) .qz-matching-item:focus-visible {
  outline: 3px solid var(--qz-primary-focus-ring, rgba(64, 158, 255, 0.25));
  outline-offset: 2px;
}

.qz-matching.qz-answered .qz-matching-item {
  cursor: default;
  pointer-events: none;
}

.qz-matching-item.qz-picked {
  border-color: var(--qz-primary, #409eff);
  background: var(--qz-primary-selected-bg, rgba(64, 158, 255, 0.09));
}

.qz-matching-item.qz-correct {
  border-color: var(--qz-correct, #67c23a);
  background: rgba(103, 194, 58, 0.08);
}

.qz-matching-item.qz-wrong {
  border-color: var(--qz-wrong, #f56c6c);
  background: rgba(245, 108, 108, 0.08);
}

.qz-matching-hint {
  margin: 10px 0 0;
  color: #6c838c;
  font-size: 13px;
  line-height: 1.5;
}

.qz-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 480px) {
  .qz-matching-item {
    padding: 10px;
    font-size: 13px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .qz-matching-item {
    transition: none;
  }
}
</style>
